import { useState } from "react";

import { Button } from "~/components/Button";
import { Input } from "~/components/Input";
import { formatConfidence } from "~/lib/confidence";
import { useDocument } from "~/lib/documents";
import type { EventState } from "~/lib/events";
import {
  matchCandidateLabel,
  matchCandidates,
  readFields,
  type MatchCandidate,
  type Task,
  type Verdict,
} from "~/lib/tasks";

import * as css from "~/components/ReviewCard.css";

export interface ReviewCardProps {
  /** The review-gate (or exception-gate) task the human is resolving. */
  task: Task;
  /** The verdict the gate gates on — the machine's claim. */
  verdict: Verdict;
  /** Exception gate (`set filevine project id`): the document couldn't be
   *  matched to a case, so the card collects a Filevine project id instead of
   *  approve/reject. */
  isException: boolean;
  /** Whether the gate is still actionable (not terminal, not mid-flight). When
   *  false the card is read-only — the row itself shows Reopen / Processing. */
  actionable: boolean;
  onApprove: () => void;
  onReject: () => void;
  onResolve: (filevineProjectID: number) => void;
  /** Which transition is mid-flight on this task, so the button labels its own
   *  pending state (mirrors TaskTable's Mark done / Reject). */
  inFlight: boolean;
  inFlightState: EventState | undefined;
  resolvePending: boolean;
}

/**
 * ReviewCard is the human review gate (TMF-227): it shows the document, states
 * the machine's claim in one plain-language sentence ending in the question the
 * human answers, lists the fields the machine read, and offers Approve / Reject
 * — or, for the `set filevine project id` exception gate, a Filevine-project-id
 * input that assigns the case and lets the engine file the document.
 *
 * The frontend never asserts lifecycle state: Approve/Reject report the human's
 * action through the same event mechanics the old Mark done / Reject used; the
 * engine owns all state.
 */
export function ReviewCard({
  task,
  verdict,
  isException,
  actionable,
  onApprove,
  onReject,
  onResolve,
  inFlight,
  inFlightState,
  resolvePending,
}: ReviewCardProps) {
  const [projectId, setProjectId] = useState("");

  const documentId = task._embedded?.document?.id;
  // The card only mounts when its row is expanded (TaskRow owns the collapse),
  // so fetch the document + presigned preview_url whenever the card renders.
  const { data: document, isLoading: documentLoading } = useDocument(documentId);
  const documentName =
    document?.name ?? task._embedded?.document?.name ?? "Document";
  const previewURL = document?.preview_url;
  const extractions = readFields(verdict);
  // The cases the match step proposed, when it found some but couldn't choose
  // (TMF-370). They fill the input below; they never replace it.
  const candidates = matchCandidates(verdict);

  const resolveGate = () => {
    const parsed = Number.parseInt(projectId.trim(), 10);
    if (!Number.isInteger(parsed) || parsed <= 0) return;
    onResolve(parsed);
  };

  // Decision block first — the claim, the fields the machine read, and the
  // actions sit together above the (tall) document preview, so the reviewer
  // never scrolls past the evidence to find the buttons.
  return (
    <div className={css.panel}>
      <span className={css.docName}>{documentName}</span>

      <p className={css.claim}>
        {claimSentence(task.type, verdict, candidates.length)}
      </p>

      {extractions.length > 0 ? (
        <div className={css.fields}>
          <span className={css.sectionLabel}>What the machine read</span>
          {extractions.map((e) => (
            <div key={e.id} className={css.fieldRow}>
              <span className={css.fieldLabel}>{humanizeFieldType(e.type)}</span>
              <span className={css.fieldValue}>
                {e.value || "—"}
                {typeof e.confidence === "number" ? (
                  <span className={css.fieldConfidence}>
                    {formatConfidence(e.confidence)}
                  </span>
                ) : null}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {actionable ? (
        isException ? (
          <>
            <CandidateOptions
              candidates={candidates}
              selected={projectId}
              onPick={setProjectId}
            />
            <div className={css.actions}>
              <Input
                className={css.gateInput}
                type="number"
                min={1}
                placeholder="Filevine project id"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
              />
              <Button
                type="button"
                variant="primary"
                onClick={resolveGate}
                disabled={resolvePending}
              >
                {resolvePending ? "Filing…" : "Assign case & file"}
              </Button>
            </div>
          </>
        ) : (
          <div className={css.actions}>
            <Button
              type="button"
              variant="primary"
              onClick={onApprove}
              disabled={inFlight}
            >
              {inFlightState === "complete" ? "Approving…" : "Approve"}
            </Button>
            <Button
              type="button"
              variant="danger"
              onClick={onReject}
              disabled={inFlight}
            >
              {inFlightState === "rejected" ? "Rejecting…" : "Reject"}
            </Button>
          </div>
        )
      ) : null}

      {documentId === undefined ? null : previewURL ? (
        <DocumentPreview key={documentId} url={previewURL} title={documentName} />
      ) : (
        <div className={css.previewEmpty}>
          {documentLoading ? "Loading document…" : "No preview available"}
        </div>
      )}
    </div>
  );
}

/**
 * The cases the match step proposed but would not choose between (TMF-370).
 * Picking one fills the Filevine-id input below rather than resolving the gate:
 * the list proposes, the human still commits, and an id that isn't on the list
 * stays typeable — which is the only way to file a document whose matter the
 * search never surfaced.
 */
function CandidateOptions({
  candidates,
  selected,
  onPick,
}: {
  candidates: MatchCandidate[];
  selected: string;
  onPick: (projectID: string) => void;
}) {
  if (candidates.length === 0) return null;
  return (
    <div className={css.candidates}>
      <span className={css.sectionLabel}>Cases the search found</span>
      <div className={css.candidateOptions}>
        {candidates.map((candidate) => {
          const value = String(candidate.project_id);
          return (
            <Button
              key={candidate.project_id}
              type="button"
              variant={selected === value ? "primary" : "secondary"}
              aria-pressed={selected === value}
              onClick={() => onPick(value)}
            >
              {matchCandidateLabel(candidate)}
            </Button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * DocumentPreview sizes itself to the document so the card never scrolls: an
 * image renders at its natural aspect ratio across the card's full width, and
 * a PDF gets the browser's viewer in a full-page (letter-aspect) frame with
 * the page fit to the width (`#view=FitH` — a URL fragment, so it never
 * touches the presigned signature). The API doesn't expose the object's
 * content type, so it's probed: an <img> loads real images and errors on
 * PDFs.
 */
function DocumentPreview({ url, title }: { url: string; title: string }) {
  const [isPdf, setIsPdf] = useState(false);

  if (isPdf) {
    return (
      <iframe
        className={css.previewPdf}
        src={`${url}#view=FitH`}
        title={title}
      />
    );
  }
  return (
    <img
      className={css.previewImage}
      src={url}
      alt={title}
      onError={() => setIsPdf(true)}
    />
  );
}

/** A claim builder turns a verdict into one plain-language sentence ending in
 *  the question the human answers. */
type ClaimFn = (v: Verdict) => string;

const confidenceParen = (v: Verdict): string =>
  typeof v.confidence === "number" ? ` (${formatConfidence(v.confidence)} confident)` : "";

const detailClause = (v: Verdict): string => (v.detail.trim() ? ` ${v.detail.trim()}` : "");

// One sentence per gate type, keyed by task type. Each derives its specifics
// from the verdict (conclusion / confidence / detail) and ends in the question
// the human answers — natural language, not label soup (TMF-227).
const CLAIM_BY_TYPE: Record<string, ClaimFn> = {
  "review identity verdict": (v) =>
    `Vision read the identity document and believes it ${
      v.conclusion === "no" ? "does not match" : "matches"
    } the client on file${confidenceParen(v)}.${detailClause(v)} Is this the client?`,

  "review minor status verdict": (v) =>
    `From the date of birth it read, Vision concluded the client is ${
      v.conclusion === "minor" ? "a minor" : "an adult"
    }${confidenceParen(v)}.${detailClause(v)} Is that right?`,

  "review split verdict": (v) =>
    `Vision split this packet into the page ranges shown below — Designee Authorization, HIPAA authorization, and the signed agreement${confidenceParen(
      v,
    )}.${detailClause(v)} Are these ranges right?`,

  "set filevine project id": (v) =>
    `This document couldn't be matched to a case automatically.${detailClause(
      v,
    )} Enter the Filevine project it belongs to so it can be filed.`,
};

const defaultClaim: ClaimFn = (v) =>
  `${v.tool || "The system"} reached the conclusion ${
    v.conclusion ? `"${v.conclusion}"` : "shown below"
  }${confidenceParen(v)}.${detailClause(v)} Does this look right?`;

function claimSentence(
  taskType: string,
  verdict: Verdict,
  candidateCount: number,
): string {
  // Candidates mean the machine finished and found cases it wouldn't choose
  // between — saying the check "couldn't complete" over a list of options would
  // misdescribe what the human is looking at (TMF-370).
  if (candidateCount > 0) {
    return `This document couldn't be matched to one case automatically.${detailClause(
      verdict,
    )} Pick the case it belongs to, or enter another Filevine project id.`;
  }
  if (verdict.status === "error") {
    return `This automated check couldn't complete.${detailClause(verdict)} Please open the document, review it, and decide.`;
  }
  return (CLAIM_BY_TYPE[taskType] ?? defaultClaim)(verdict);
}

// humanizeFieldType turns a PascalCase field-type slug into spaced words while
// keeping all-caps acronyms intact: "ClientName" → "Client Name", "DOB" → "DOB".
function humanizeFieldType(type: string): string {
  return type
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/([A-Z]+)([A-Z][a-z])/g, "$1 $2");
}
